import React, { useState, useEffect, useCallback } from 'react';
import { Star, Calendar, User, MessageSquare, Filter, Search, Building2 } from 'lucide-react';
import { getEmployeeRatings, getEmployeeRatingStats, type Rating, type EmployeeRatingStats } from '../services/ratingApi';
import { useI18n } from '../contexts/I18nContext';
import { useToast } from '../components/Toast';
import { useAuth } from '../contexts/AuthContext';
import Pagination from '../components/Pagination';

const Avaliacoes: React.FC = () => {
  const { t } = useI18n();
  const { showError } = useToast();
  const { user } = useAuth();
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [stats, setStats] = useState<EmployeeRatingStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalRatings, setTotalRatings] = useState(0);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);

  // Se o usuário for funcionário, carregar suas próprias avaliações
  useEffect(() => {
    if (user?.role === 'employee' && user?.id) {
      setSelectedEmployeeId(user.id);
    }
  }, [user]);

  const loadRatings = useCallback(async () => {
    if (!selectedEmployeeId) return;

    setIsLoading(true);
    try {
      const params: any = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      };

      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const response = await getEmployeeRatings(selectedEmployeeId, params);
      
      // A API retorna { ratings: [], total: number }
      if (response && typeof response === 'object') {
        const ratingsArray = (response as any).ratings || (Array.isArray(response) ? response : []);
        const total = (response as any).total || ratingsArray.length;
        setRatings(ratingsArray);
        setTotalRatings(total);
      } else if (Array.isArray(response)) {
        setRatings(response);
        setTotalRatings(response.length);
      } else {
        setRatings([]);
        setTotalRatings(0);
      }
    } catch (error: any) {
      console.error('Erro ao carregar avaliações:', error);
      showError(error.response?.data?.error || 'Erro ao carregar avaliações');
    } finally {
      setIsLoading(false);
    }
  }, [selectedEmployeeId, currentPage, itemsPerPage, filterStatus, showError]);

  const loadStats = useCallback(async () => {
    if (!selectedEmployeeId) return;

    try {
      const statsData = await getEmployeeRatingStats(selectedEmployeeId);
      setStats(statsData);
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, [selectedEmployeeId]);

  useEffect(() => {
    if (selectedEmployeeId) {
      loadRatings();
      loadStats();
    }
  }, [selectedEmployeeId, loadRatings, loadStats]);

  const filteredRatings = ratings.filter(rating => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        rating.client_name?.toLowerCase().includes(searchLower) ||
        rating.property_name?.toLowerCase().includes(searchLower) ||
        rating.comment?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const renderStars = (rating: number) => {
    return (
      <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            fill={star <= rating ? '#fbbf24' : 'none'}
            color={star <= rating ? '#fbbf24' : '#d1d5db'}
          />
        ))}
        <span style={{ marginLeft: '8px', fontSize: '14px', fontWeight: '500' }}>
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '10px', color: '#1f2937' }}>
          {t('menu.history')}
        </h1>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>
          {t('ratings.history.subtitle') || 'Visualize todas as avaliações recebidas'}
        </p>
      </div>

      {/* Estatísticas */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>
              {t('ratings.stats.average') || 'Avaliação Média'}
            </div>
            <div style={{ fontSize: '32px', fontWeight: '600', color: '#1f2937' }}>
              {stats.average_rating.toFixed(1)}
            </div>
            {renderStars(Math.round(stats.average_rating))}
          </div>

          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>
              {t('ratings.stats.total') || 'Total de Avaliações'}
            </div>
            <div style={{ fontSize: '32px', fontWeight: '600', color: '#1f2937' }}>
              {stats.total_ratings}
            </div>
          </div>

          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>
              {t('ratings.stats.fiveStar') || '5 Estrelas'}
            </div>
            <div style={{ fontSize: '32px', fontWeight: '600', color: '#10b981' }}>
              {stats.five_star_count || 0}
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px',
        display: 'flex',
        gap: '15px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder={t('ratings.search.placeholder') || 'Buscar por cliente, propriedade ou comentário...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 10px 10px 40px',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            padding: '10px 15px',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          <option value="all">{t('ratings.filter.all') || 'Todos os Status'}</option>
          <option value="approved">{t('ratings.filter.approved') || 'Aprovadas'}</option>
          <option value="pending">{t('ratings.filter.pending') || 'Pendentes'}</option>
          <option value="submitted">{t('ratings.filter.submitted') || 'Enviadas'}</option>
        </select>
      </div>

      {/* Lista de Avaliações */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ color: '#6b7280' }}>{t('reports.loadingRatings')}</div>
        </div>
      ) : filteredRatings.length === 0 ? (
        <div style={{
          background: 'white',
          padding: '60px 20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center',
        }}>
          <Star size={48} color="#d1d5db" style={{ marginBottom: '16px' }} />
          <div style={{ color: '#6b7280', fontSize: '16px' }}>
            {t('ratings.history.empty') || 'Nenhuma avaliação encontrada'}
          </div>
        </div>
      ) : (
        <>
          <div style={{
            display: 'grid',
            gap: '16px',
            marginBottom: '20px',
          }}>
            {filteredRatings.map((rating) => (
              <div
                key={rating.id}
                style={{
                  background: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  borderLeft: '4px solid',
                  borderLeftColor: rating.rating >= 4 ? '#10b981' : rating.rating >= 3 ? '#f59e0b' : '#ef4444',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      {renderStars(rating.rating)}
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: rating.status === 'approved' ? '#d1fae5' : rating.status === 'pending' ? '#fef3c7' : '#e5e7eb',
                        color: rating.status === 'approved' ? '#065f46' : rating.status === 'pending' ? '#92400e' : '#374151',
                      }}>
                        {rating.status === 'approved' ? 'Aprovada' : rating.status === 'pending' ? 'Pendente' : 'Enviada'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
                      {rating.client_name && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <User size={14} />
                          <span>{rating.client_name}</span>
                        </div>
                      )}
                      {rating.property_name && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Building2 size={14} />
                          <span>{rating.property_name}</span>
                        </div>
                      )}
                      {rating.appointment_date && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Calendar size={14} />
                          <span>{formatDate(rating.appointment_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    {formatDate(rating.created_at)}
                  </div>
                </div>

                {rating.comment && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#374151',
                    display: 'flex',
                    gap: '8px',
                  }}>
                    <MessageSquare size={16} color="#6b7280" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div>{rating.comment}</div>
                  </div>
                )}

                {/* Avaliações específicas */}
                {(rating.punctuality_rating || rating.quality_rating || rating.communication_rating || rating.cleanliness_rating) && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '12px',
                    fontSize: '12px',
                  }}>
                    {rating.punctuality_rating && (
                      <div>
                        <div style={{ color: '#6b7280', marginBottom: '4px' }}>{t('common.punctuality')}</div>
                        {renderStars(rating.punctuality_rating)}
                      </div>
                    )}
                    {rating.quality_rating && (
                      <div>
                        <div style={{ color: '#6b7280', marginBottom: '4px' }}>{t('common.quality')}</div>
                        {renderStars(rating.quality_rating)}
                      </div>
                    )}
                    {rating.communication_rating && (
                      <div>
                        <div style={{ color: '#6b7280', marginBottom: '4px' }}>{t('common.communication')}</div>
                        {renderStars(rating.communication_rating)}
                      </div>
                    )}
                    {rating.cleanliness_rating && (
                      <div>
                        <div style={{ color: '#6b7280', marginBottom: '4px' }}>Limpeza</div>
                        {renderStars(rating.cleanliness_rating)}
                      </div>
                    )}
                  </div>
                )}

                {rating.employee_response && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#eff6ff',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#1e40af',
                    borderLeft: '3px solid #3b82f6',
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>{t('common.yourResponse')}</div>
                    <div>{rating.employee_response}</div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {totalRatings > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalRatings}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Avaliacoes;

